import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ClarificationRequestsPanel } from "@/features/clarification";
import { useRightSidebarUi } from "../../contexts";
import type { RightSidebarTab } from "../../types";
import { ClaudiaPanel } from "./claudia-panel";
import { GuardianPanel } from "./guardian-panel";
import { TeamsPanel } from "./teams-panel";
import { VersionsPanel } from "./versions-panel";

// Panel display names keyed by tab ID
const panelLabels: Record<RightSidebarTab, string> = {
  versions: "Change History",
  guardian: "Quality Guardian",
  cr: "Clarification Requests",
  teams: "Teams",
  claudia: "Claudia",
};

export function RightSidebar() {
  const { activePanel, toggleSidebar } = useRightSidebarUi();

  // Announce panel changes to screen readers
  const [announcement, setAnnouncement] = useState("");
  const prevPanelRef = useRef(activePanel);

  useEffect(() => {
    if (prevPanelRef.current !== activePanel) {
      const label = panelLabels[activePanel];
      if (label) {
        setAnnouncement(`${label} panel selected`);
      }
      prevPanelRef.current = activePanel;
    }
  }, [activePanel]);

  // Focus management: move focus to panel content on panel switch
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    void activePanel;
    if (panelRef.current) {
      panelRef.current.focus();
    }
  }, [activePanel]);

  return (
    <aside
      aria-label="Project tools"
      className="flex flex-col bg-surface-raised h-full overflow-hidden border-l"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          toggleSidebar();
        }
      }}
    >
      {/* Panel header: title + close button (Claudia renders its own) */}
      {activePanel !== "claudia" && (
        <div className="flex items-center justify-between border-b shrink-0 px-4 h-12">
          <h2 className="text-sm font-semibold">{panelLabels[activePanel]}</h2>
          <Button variant="ghost" size="icon-sm" onClick={toggleSidebar} aria-label="Close panel">
            <X className="size-4" />
          </Button>
        </div>
      )}

      <section
        ref={panelRef}
        id={`panel-${activePanel}`}
        aria-label={panelLabels[activePanel]}
        tabIndex={-1}
        className="flex min-h-0 flex-1 flex-col overflow-hidden outline-none"
      >
        {activePanel === "claudia" && <ClaudiaPanel />}
        {activePanel === "versions" && <VersionsPanel />}
        {activePanel === "guardian" && <GuardianPanel />}
        {activePanel === "cr" && <ClarificationRequestsPanel />}
        {activePanel === "teams" && <TeamsPanel />}
      </section>

      {/* Screen reader announcements */}
      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </aside>
  );
}
